/**
 * Anthropic Messages API — minimal client wrapper for the Coach LLM Narrator.
 *
 * Uses fetch() directly against the public Anthropic API endpoint:
 *   https://api.anthropic.com/v1/messages
 *
 * Why a thin wrapper here instead of the @anthropic-ai/sdk package:
 *   - Bundle weight: this is a single-purpose call site, not a general SDK
 *     consumer. The SDK pulls in axios + retry logic + streaming machinery
 *     we don't need for a one-shot synthesis paragraph.
 *   - Browser CORS: Anthropic's API supports the
 *     `anthropic-dangerous-direct-browser-access: true` header for direct
 *     browser calls. The Electron renderer runs in a Chromium context, so
 *     we use that path. (For a future hardening pass we could move calls
 *     into the Electron main process via IPC; out of scope for v1.)
 *   - Key handling: the API key lives in user settings. The client receives
 *     it per-call so it never has to import from the React store layer.
 *
 * Returns: { ok: true, text, inputTokens, outputTokens, model } on success,
 *          { ok: false, error: string, status?: number } on failure.
 *
 * Errors are NEVER thrown — callers always get a structured result so the
 * UI can surface "API failed, falling back to templated narrator" cleanly.
 */

const ANTHROPIC_URL = 'https://api.anthropic.com/v1/messages';
const ANTHROPIC_VERSION = '2023-06-01';

// Default model — Sonnet 4.5 is the right balance for short coaching prose:
// fast, smart enough to synthesize, cheap enough to call freely.
// Override per-call via opts.model.
export const DEFAULT_MODEL = 'claude-sonnet-4-5-20250929';
export const DEFAULT_MAX_TOKENS = 400;

/**
 * Single non-streaming call to the Messages API.
 *
 * @param {Object} args
 * @param {string} args.apiKey   User's Anthropic API key (sk-ant-…)
 * @param {string} args.system   System prompt (defines the coach voice)
 * @param {string} args.user     User message (the actual observations + ask)
 * @param {string} [args.model]  Model id; defaults to DEFAULT_MODEL
 * @param {number} [args.maxTokens]  Output cap; defaults to DEFAULT_MAX_TOKENS
 * @param {AbortSignal} [args.signal]  For cancelling in-flight requests on unmount
 * @returns {Promise<{ok: true, text: string, inputTokens: number, outputTokens: number, model: string} | {ok: false, error: string, status?: number}>}
 */
export const callAnthropic = async ({
  apiKey,
  system,
  user,
  model = DEFAULT_MODEL,
  maxTokens = DEFAULT_MAX_TOKENS,
  signal,
}) => {
  if (!user || typeof user !== 'string') {
    return { ok: false, error: 'Empty user prompt' };
  }

  // Phase 14 hardening: when running in Electron, prefer the main-process
  // IPC route. Main reads the API key from settings.json itself, so the
  // renderer never has to ship the key in fetch headers / closures /
  // request bodies. The key still lives in renderer state for the Settings
  // input field, but it doesn't have to travel through the analyzer call
  // path. Falls back to the direct-fetch path below when the IPC bridge
  // isn't available (browser/dev mode).
  if (typeof window !== 'undefined' && window.electronAPI?.coachLlmCall) {
    try {
      const result = await window.electronAPI.coachLlmCall({ system, user, model, maxTokens });
      // Main returns the same shape callAnthropic produces, so just
      // pass it through.
      return result;
    } catch (e) {
      return { ok: false, error: `IPC error: ${e?.message || e}` };
    }
  }

  // Direct-fetch fallback (browser/dev mode). Requires an apiKey arg.
  if (!apiKey || typeof apiKey !== 'string' || apiKey.length < 20) {
    return { ok: false, error: 'Missing or malformed API key' };
  }

  // Prompt caching — wrap the system prompt in a content block with
  // `cache_control: { type: 'ephemeral' }` so Anthropic caches the static
  // portion for ~5 minutes. First call costs +25% on the cached tokens
  // (cache write); every subsequent call within the TTL costs only 10%
  // of the base input rate for cached tokens. Net win after the 2nd
  // call. The system prompt is identical across every coaching call, so
  // this is pure free money on a typical multi-call session (dashboard
  // refresh + weekly digest in one sitting = 2-3 calls within a 5-min
  // window).
  //
  // The body field shape changes from a string to an array of blocks
  // when caching is on — that's the API's required format.
  const body = {
    model,
    max_tokens: maxTokens,
    system: system
      ? [{ type: 'text', text: system, cache_control: { type: 'ephemeral' } }]
      : '',
    messages: [{ role: 'user', content: user }],
  };

  let res;
  try {
    res = await fetch(ANTHROPIC_URL, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': ANTHROPIC_VERSION,
        // Required to allow direct browser-context calls (Electron renderer
        // counts as browser). Without this the API rejects the request.
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if (e?.name === 'AbortError') return { ok: false, error: 'Request cancelled' };
    return { ok: false, error: `Network error: ${e?.message || e}` };
  }

  let json;
  try {
    json = await res.json();
  } catch {
    return { ok: false, error: `Non-JSON response (status ${res.status})`, status: res.status };
  }

  if (!res.ok) {
    const apiMsg = json?.error?.message || json?.message || 'Unknown API error';
    return { ok: false, error: apiMsg, status: res.status };
  }

  // Messages API returns content as an array of blocks. The text we want is
  // the first text block's value. Defensive in case of empty content.
  const textBlock = Array.isArray(json?.content)
    ? json.content.find(c => c?.type === 'text')
    : null;
  const text = textBlock?.text || '';
  if (!text) return { ok: false, error: 'API returned no text content' };

  // Cache telemetry — Anthropic returns cache_creation_input_tokens (paid
  // at +25%) and cache_read_input_tokens (paid at -90%). input_tokens is
  // the NON-cached portion. We surface all three so the cost estimator
  // can price each correctly and the Settings UI can show cache hit rate.
  const usage = json?.usage || {};
  return {
    ok: true,
    text: text.trim(),
    inputTokens: usage.input_tokens || 0,
    outputTokens: usage.output_tokens || 0,
    cacheCreationTokens: usage.cache_creation_input_tokens || 0,
    cacheReadTokens: usage.cache_read_input_tokens || 0,
    model: json?.model || model,
  };
};

/**
 * Approximate $/1M-token cost calculator for budgeting telemetry. Matches
 * Anthropic's published pricing for the Sonnet/Haiku/Opus families circa
 * 2026, INCLUDING prompt-cache write/read multipliers:
 *
 *   - Cache write costs 1.25× the base input rate (one-time tax for the
 *     5-minute TTL ephemeral cache)
 *   - Cache read costs 0.10× the base input rate (the actual win)
 *   - Non-cached input tokens cost the base rate
 *   - Output tokens are unaffected by caching
 *
 * Off-by-a-cent okay; this is a rough budget meter, not invoicing.
 * Backwards-compatible: callers that don't pass cache token counts (the
 * pre-caching test fixtures) get the old 2-arg behavior.
 *
 * Returns total dollar cost as a number (e.g., 0.0083).
 *
 * @param {string} model
 * @param {number} inputTokens          Non-cached input tokens
 * @param {number} outputTokens
 * @param {number} [cacheCreationTokens=0]  Tokens written to cache this call
 * @param {number} [cacheReadTokens=0]      Tokens served from cache this call
 */
export const estimateCostUSD = (model, inputTokens, outputTokens, cacheCreationTokens = 0, cacheReadTokens = 0) => {
  const family = (model || '').toLowerCase();
  // Per-1M base rates (input, output)
  let inputRate, outputRate;
  if (family.includes('haiku'))      { inputRate = 0.25; outputRate = 1.25; }
  else if (family.includes('opus'))  { inputRate = 15;   outputRate = 75;   }
  else                                { inputRate = 3;    outputRate = 15;   }
  // Anthropic's published cache pricing multipliers
  const CACHE_WRITE_MULT = 1.25;
  const CACHE_READ_MULT  = 0.10;
  const total =
    (inputTokens          * inputRate)                       +
    (cacheCreationTokens  * inputRate * CACHE_WRITE_MULT)    +
    (cacheReadTokens      * inputRate * CACHE_READ_MULT)     +
    (outputTokens         * outputRate);
  return total / 1_000_000;
};
